import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { EXISTENTIAL_PILLAR_NAMES, ExistentialPillarName, ExistentialStage } from '../constants/existentialPillars';

export interface ExistentialPillar {
  id: string;
  user_id: string;
  pillar_name: ExistentialPillarName;
  level: number;
  xp: number;
  current_level_xp: number;
  xp_to_next_level: number;
  stage: ExistentialStage;
  created_at: string;
  updated_at: string;
}

interface ExistentialPillarsState {
  pillars: ExistentialPillar[];
  isLoading: boolean;
  error: string | null;

  fetchPillars: () => Promise<void>;
  initializePillars: () => Promise<void>;
  addXP: (pillarName: ExistentialPillarName, xpAmount: number) => Promise<void>;
  getPillar: (name: ExistentialPillarName) => ExistentialPillar | undefined;
  getTotalLevel: () => number;
  getAverageLevel: () => number;
  getStrongestPillar: () => ExistentialPillar | null;
  getWeakestPillar: () => ExistentialPillar | null;
  getTotalXP: () => number;
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useExistentialPillarsStore = create<ExistentialPillarsState>((set, get) => ({
  pillars: [],
  isLoading: false,
  error: null,

  fetchPillars: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('existential_pillars')
        .select('*')
        .eq('user_id', user.id)
        .order('pillar_name');
      // Signed out while it read: its pillars don't land, and none are made for the next account.
      if (at !== epoch) return;

      if (error) throw error;

      if (!data || data.length === 0) {
        await get().initializePillars();
        return;
      }

      set({ pillars: data as ExistentialPillar[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  initializePillars: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const { error: initError } = await supabase.rpc('initialize_existential_pillars', {
        p_user_id: user.id
      });

      if (initError) {
        const newPillars = EXISTENTIAL_PILLAR_NAMES.map(name => ({
          user_id: user.id,
          pillar_name: name,
          level: 1,
          xp: 0,
          current_level_xp: 0,
          xp_to_next_level: 100,
          stage: 'Seeking'
        }));

        const { error: insertError } = await supabase
          .from('existential_pillars')
          .upsert(newPillars, { onConflict: 'user_id,pillar_name' });

        if (insertError) throw insertError;
      }

      const { data, error } = await supabase
        .from('existential_pillars')
        .select('*')
        .eq('user_id', user.id)
        .order('pillar_name');

      if (at !== epoch) return; // signed out while it ran
      if (error) throw error;

      set({ pillars: data as ExistentialPillar[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  addXP: async (pillarName, xpAmount) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      // ⚠️⚠️ Signed out while it asked: the pillar is found by NAME, so the next account's
      // pillar of that name would take this account's XP.
      if (!user || at !== epoch) return;

      const currentPillar = get().pillars.find(p => p.pillar_name === pillarName);
      if (currentPillar) {
        const newXP = currentPillar.xp + xpAmount;

        const { error: updateError } = await supabase
          .from('existential_pillars')
          .update({ xp: newXP })
          .eq('id', currentPillar.id);

        if (updateError) throw updateError;
      }

      await get().fetchPillars();
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message });
    }
  },

  getPillar: (name) => {
    return get().pillars.find(p => p.pillar_name === name);
  },

  getTotalLevel: () => {
    const pillars = get().pillars;
    if (pillars.length === 0) return 0;
    return pillars.reduce((sum, p) => sum + p.level, 0);
  },

  getAverageLevel: () => {
    const pillars = get().pillars;
    if (pillars.length === 0) return 1;
    return Math.round(pillars.reduce((sum, p) => sum + p.level, 0) / pillars.length);
  },

  getStrongestPillar: () => {
    const pillars = get().pillars;
    if (pillars.length === 0) return null;
    return pillars.reduce((max, p) => (p.xp > max.xp ? p : max), pillars[0]);
  },

  getWeakestPillar: () => {
    const pillars = get().pillars;
    if (pillars.length === 0) return null;
    return pillars.reduce((min, p) => (p.xp < min.xp ? p : min), pillars[0]);
  },

  getTotalXP: () => {
    const pillars = get().pillars;
    return pillars.reduce((sum, p) => sum + p.xp, 0);
  },

  /** Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does not reload the page. */
  clearStore: () => {
    epoch += 1;
    set({ pillars: [], isLoading: false, error: null });
  },
}));

onSignOut(() => useExistentialPillarsStore.getState().clearStore());
